(() => {
  if (customElements.get('rmo-product-print')) return;

  const normalizeId = (value) => {
    if (value === undefined || value === null) return '';
    const id = String(value);
    const segments = id.split('/');
    return segments[segments.length - 1];
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  };

  class RmoProductPrint extends HTMLElement {
    connectedCallback() {
      if (this.dataset.rmoPrintReady === 'true') return;

      this.config = this.readJson('[data-rmo-product-print-config]', {});
      this.variants = this.readJson('[data-rmo-product-print-variants]', []);
      this.variantMap = new Map(
        this.variants.map((variant) => [normalizeId(variant.id), variant])
      );
      this.selectedVariantId = normalizeId(this.dataset.initialVariantId);
      this.handleClick = this.handleClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleAfterPrint = this.handleAfterPrint.bind(this);
      this.handlePostPrintInteraction =
        this.handlePostPrintInteraction.bind(this);

      this.addEventListener('click', this.handleClick);
      document.addEventListener('rmo:variant-change', this.handleVariantChange);
      window.addEventListener('afterprint', this.handleAfterPrint);
      this.dataset.rmoPrintReady = 'true';
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('rmo:variant-change', this.handleVariantChange);
      window.removeEventListener('afterprint', this.handleAfterPrint);
      this.removePostPrintCleanupListeners();
      this.removePrintPortal();
    }

    readJson(selector, fallback) {
      const source = this.querySelector(selector);
      if (!source) return fallback;

      try {
        return JSON.parse(source.textContent);
      } catch (_error) {
        return fallback;
      }
    }

    handleVariantChange(event) {
      const detail = event.detail || {};
      const eventProductId = normalizeId(
        detail.productId || detail.product?.id || detail.variant?.product_id
      );
      const componentProductId = normalizeId(this.dataset.productId);

      if (eventProductId && eventProductId !== componentProductId) return;

      const candidateId = normalizeId(
        detail.variantId || detail.variant?.id || detail.id
      );

      if (this.variantMap.has(candidateId)) {
        this.selectedVariantId = candidateId;
      }
    }

    handleClick(event) {
      const button = event.target.closest('[data-rmo-print-mode]');
      if (!button || !this.contains(button)) return;

      const mode = button.dataset.rmoPrintMode;
      if (mode !== 'standard' && mode !== 'contractor') return;

      const variant = this.resolveSelectedVariant();
      if (!variant) return;

      this.removePrintPortal();
      this.printPortal = this.buildPrintPortal(mode, variant);
      document.body.appendChild(this.printPortal);
      document.body.classList.add('rmo-print-active');
      this.printPortal.getBoundingClientRect();
      this.prepareAndPrint();
    }

    async prepareAndPrint() {
      if (!this.printPortal || this.dataset.rmoPrintPreparing === 'true') return;

      this.dataset.rmoPrintPreparing = 'true';
      const pause = (milliseconds) =>
        new Promise((resolve) => window.setTimeout(resolve, milliseconds));
      const imageTasks = [...this.printPortal.querySelectorAll('img')]
        .filter((image) => !image.complete || image.naturalWidth === 0)
        .map((image) => {
          if (typeof image.decode === 'function') {
            return image.decode().catch(() => undefined);
          }

          return new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });
        });

      const readiness = [...imageTasks];
      if (document.fonts?.ready) readiness.push(document.fonts.ready);

      if (readiness.length > 0) {
        await Promise.race([Promise.allSettled(readiness), pause(1800)]);
      }

      if (this.printPortal?.isConnected) window.print();
      delete this.dataset.rmoPrintPreparing;
    }

    resolveSelectedVariant() {
      const inputVariantId = this.findCurrentVariantInput();
      if (this.variantMap.has(inputVariantId)) {
        this.selectedVariantId = inputVariantId;
      }

      return (
        this.variantMap.get(this.selectedVariantId) ||
        this.variants[0] ||
        null
      );
    }

    findCurrentVariantInput() {
      const section = this.closest('[id^="shopify-section-"]');
      const selectors = [
        'form[action*="/cart/add"] input[name="id"]:checked',
        'form[action*="/cart/add"] select[name="id"]',
        'form[action*="/cart/add"] input[name="id"]'
      ];
      const scopes = section ? [section, document] : [document];

      for (const scope of scopes) {
        for (const selector of selectors) {
          const inputs = scope.querySelectorAll(selector);
          for (const input of inputs) {
            const candidateId = normalizeId(input.value);
            if (!input.disabled && this.variantMap.has(candidateId)) {
              return candidateId;
            }
          }
        }
      }

      return '';
    }

    buildPrintPortal(mode, variant) {
      const labels = this.config.labels || {};
      const portal = createElement(
        'div',
        `rmo-product-print-portal rmo-product-print-portal--${mode}`
      );
      portal.setAttribute('aria-hidden', 'true');

      const sheet = createElement('article', 'rmo-product-print-sheet');
      const header = createElement('header', 'rmo-product-print-sheet__header');
      const brand = createElement(
        'div',
        'rmo-product-print-sheet__brand',
        this.config.shopName
      );
      const sheetType = createElement(
        'div',
        'rmo-product-print-sheet__type',
        mode === 'standard' ? labels.standardSheet : labels.contractorSheet
      );
      const title = createElement(
        'div',
        'rmo-product-print-sheet__title',
        this.config.productTitle
      );
      title.setAttribute('role', 'heading');
      title.setAttribute('aria-level', '1');
      header.append(brand, sheetType, title);
      sheet.appendChild(header);

      const overview = createElement(
        'section',
        'rmo-product-print-sheet__overview'
      );

      if (variant.image?.src) {
        const image = createElement('img', 'rmo-product-print-sheet__image');
        image.src = variant.image.src;
        image.alt = variant.image.alt || this.config.productTitle || '';
        overview.appendChild(image);
      }

      const facts = createElement('dl', 'rmo-product-print-sheet__facts');

      if (!this.config.hasOnlyDefaultVariant && variant.title) {
        this.appendFact(facts, labels.variant, variant.title);
      }

      if (variant.sku) {
        this.appendFact(facts, labels.sku, variant.sku);
      }

      if (this.config.manufacturer) {
        this.appendFact(facts, labels.manufacturer, this.config.manufacturer);
      }

      if (mode === 'standard' && variant.price) {
        this.appendFact(facts, labels.price, variant.price, true);
      }

      if (this.config.productUrl) {
        const term = createElement('dt', '', labels.productPage);
        const definition = createElement('dd');
        const link = createElement('a', '', this.config.productUrl);
        link.href = this.config.productUrl;
        definition.appendChild(link);
        facts.append(term, definition);
      }

      overview.appendChild(facts);
      sheet.appendChild(overview);

      if (this.config.overview) {
        const overviewSection = createElement(
          'section',
          'rmo-product-print-sheet__description'
        );
        overviewSection.append(
          createElement('h2', 'rmo-product-print-sheet__section-title', labels.overview),
          createElement('p', '', this.config.overview)
        );
        sheet.appendChild(overviewSection);
      }

      this.appendSourceGroup(
        sheet,
        labels.specifications,
        this.dataset.specificationsSelector
      );
      this.appendSourceGroup(sheet, labels.ada, this.dataset.adaSelector);
      this.appendSourceGroup(
        sheet,
        labels.certifications,
        this.dataset.certificationsSelector
      );

      if (mode === 'contractor') {
        sheet.appendChild(this.buildContractorFields(labels, variant));
      }

      sheet.appendChild(this.buildSupportSection(labels));
      sheet.appendChild(this.buildFooter(labels, mode));

      portal.appendChild(sheet);
      return portal;
    }

    appendFact(list, label, value, isPrice = false) {
      const term = createElement('dt', '', label);
      const definition = createElement('dd', '', value);
      if (isPrice) definition.dataset.rmoPublicPrice = 'true';
      list.append(term, definition);
    }

    appendSourceGroup(sheet, label, selector) {
      const sources = this.querySources(selector);
      const clones = sources
        .map((source) => this.sanitizeClone(source))
        .filter((clone) => this.hasPrintableContent(clone));

      if (clones.length === 0) return;

      const section = createElement(
        'section',
        'rmo-product-print-sheet__source-group'
      );
      const heading = createElement(
        'h2',
        'rmo-product-print-sheet__section-title',
        label
      );
      section.appendChild(heading);

      for (const clone of clones) {
        const content = createElement(
          'div',
          'rmo-product-print-sheet__source-content'
        );
        content.appendChild(clone);
        section.appendChild(content);
      }

      sheet.appendChild(section);
    }

    querySources(selector) {
      if (!selector) return [];

      try {
        return Array.from(document.querySelectorAll(selector));
      } catch (_error) {
        return [];
      }
    }

    sanitizeClone(source) {
      const clone = source.cloneNode(true);
      const sourceGroup = source.dataset.rmoPrintSource;

      if (sourceGroup) {
        clone.querySelectorAll('[data-rmo-print-source]').forEach((nestedSource) => {
          if (nestedSource.dataset.rmoPrintSource !== sourceGroup) {
            nestedSource.remove();
          }
        });
      }

      Array.from(clone.children).forEach((child) => {
        if (/^H[1-6]$/.test(child.tagName)) child.remove();
      });

      const removable = clone.querySelectorAll(
        'script, style, template, form, button, input, select, textarea, dialog, video, audio, [hidden], [data-rmo-no-print]'
      );
      removable.forEach((element) => element.remove());

      const allElements = [clone, ...clone.querySelectorAll('*')];
      for (const element of allElements) {
        element.removeAttribute('id');
        element.removeAttribute('aria-controls');
        element.removeAttribute('aria-describedby');
        element.removeAttribute('aria-labelledby');
        element.removeAttribute('aria-owns');
        element.removeAttribute('tabindex');
      }

      clone.querySelectorAll('details').forEach((details) => {
        details.open = true;
      });

      return clone;
    }

    hasPrintableContent(element) {
      return Boolean(
        element.textContent.trim() ||
          element.querySelector('img, svg, table, dl, ul, ol')
      );
    }

    buildContractorFields(labels, variant) {
      const section = createElement(
        'section',
        'rmo-product-print-sheet__project'
      );
      const heading = createElement(
        'h2',
        'rmo-product-print-sheet__section-title',
        labels.projectDetails
      );
      const fields = createElement(
        'div',
        'rmo-product-print-sheet__project-grid'
      );

      const reference = createElement(
        'div',
        'rmo-product-print-sheet__project-reference'
      );
      reference.appendChild(
        createElement('strong', '', this.config.productTitle)
      );
      if (!this.config.hasOnlyDefaultVariant && variant.title) {
        reference.appendChild(
          createElement('span', '', `${labels.variant}: ${variant.title}`)
        );
      }
      if (variant.sku) {
        reference.appendChild(
          createElement('span', '', `${labels.sku}: ${variant.sku}`)
        );
      }

      [
        labels.projectName,
        labels.projectLocation,
        labels.preparedFor,
        labels.preparedBy,
        labels.date,
        labels.estimatedPrice
      ].forEach((label) => {
        const field = createElement(
          'div',
          'rmo-product-print-sheet__project-field'
        );
        field.appendChild(
          createElement('span', 'rmo-product-print-sheet__field-label', label)
        );
        field.appendChild(
          createElement('span', 'rmo-product-print-sheet__field-line')
        );
        fields.appendChild(field);
      });

      const notes = createElement(
        'div',
        'rmo-product-print-sheet__project-field rmo-product-print-sheet__project-field--notes'
      );
      notes.appendChild(
        createElement('span', 'rmo-product-print-sheet__field-label', labels.notes)
      );
      notes.appendChild(
        createElement('span', 'rmo-product-print-sheet__field-box')
      );
      fields.appendChild(notes);
      section.append(heading, reference, fields);

      const installation = createElement(
        'div',
        'rmo-product-print-sheet__installation'
      );
      installation.appendChild(
        createElement('strong', '', `${labels.installation}:`)
      );
      [labels.included, labels.notIncluded, labels.toBeDetermined].forEach((label) => {
        const choice = createElement('span', 'rmo-product-print-sheet__choice');
        choice.append(
          createElement('span', 'rmo-product-print-sheet__checkbox', '\u2610'),
          document.createTextNode(label)
        );
        installation.appendChild(choice);
      });
      section.appendChild(installation);

      const approval = createElement(
        'section',
        'rmo-product-print-sheet__approval'
      );
      approval.append(
        createElement('h3', 'rmo-product-print-sheet__subheading', labels.approval),
        createElement('p', '', labels.approvalStatement)
      );
      const approvalFields = createElement(
        'div',
        'rmo-product-print-sheet__approval-grid'
      );
      [labels.approvedBy, labels.date].forEach((label) => {
        const field = createElement('div', 'rmo-product-print-sheet__project-field');
        field.append(
          createElement('span', 'rmo-product-print-sheet__field-label', label),
          createElement('span', 'rmo-product-print-sheet__field-line')
        );
        approvalFields.appendChild(field);
      });
      approval.appendChild(approvalFields);
      section.appendChild(approval);
      return section;
    }

    buildSupportSection(labels) {
      const section = createElement('section', 'rmo-product-print-sheet__support');
      section.append(
        createElement('strong', '', labels.supportTitle),
        createElement('span', '', ` ${labels.supportText}`)
      );
      return section;
    }

    buildFooter(labels, mode) {
      const footer = createElement('footer', 'rmo-product-print-sheet__footer');
      const contactParts = [
        this.config.shopName,
        this.config.shopPhone,
        this.config.supportEmail,
        this.config.shopDomain
      ].filter(Boolean);
      footer.appendChild(createElement('p', '', contactParts.join(' | ')));
      footer.appendChild(createElement('p', '', labels.imageDisclaimer));
      footer.appendChild(
        createElement(
          'p',
          '',
          `${labels.currentInformation} ${this.config.currentDate || ''}`.trim()
        )
      );
      if (mode === 'contractor') {
        footer.appendChild(
          createElement(
            'p',
            'rmo-product-print-sheet__purchase-disclaimer',
            labels.purchaseDisclaimer
          )
        );
      }
      return footer;
    }

    handleAfterPrint() {
      // iOS Safari can dispatch `afterprint` while the native AirPrint sheet is
      // opening, before its preview has finished taking the print snapshot.
      // Keep the print-only portal in place until the shopper returns and next
      // interacts with the page. It remains hidden outside print media.
      if (this.dataset.rmoPrintCleanupArmed === 'true') return;

      document.addEventListener(
        'pointerdown',
        this.handlePostPrintInteraction,
        true
      );
      document.addEventListener(
        'keydown',
        this.handlePostPrintInteraction,
        true
      );
      this.dataset.rmoPrintCleanupArmed = 'true';
    }

    handlePostPrintInteraction(event) {
      if (!event.isTrusted) return;

      this.removePostPrintCleanupListeners();
      this.removePrintPortal();
    }

    removePostPrintCleanupListeners() {
      document.removeEventListener(
        'pointerdown',
        this.handlePostPrintInteraction,
        true
      );
      document.removeEventListener(
        'keydown',
        this.handlePostPrintInteraction,
        true
      );
      delete this.dataset.rmoPrintCleanupArmed;
    }

    removePrintPortal() {
      document.body.classList.remove('rmo-print-active');
      delete this.dataset.rmoPrintPreparing;
      if (this.printPortal) {
        this.printPortal.remove();
        this.printPortal = null;
      }
    }
  }

  customElements.define('rmo-product-print', RmoProductPrint);
})();
